import { isCalendarDay } from '../zonedTime';

/**
 * 🔒 A company owned by another (piece 10; decisions 0113, 7501; T3102). The
 * owned company's General Information adds an owner and ends one, the owner's
 * Subsidiaries tile lists what it owns, and the server refuses in the words
 * below. 🔨 Ownership drives nothing: no rollup, credit limit, territory or
 * permission reads it.
 */

/** What the Add Owner window sends: the owning company, an optional percent and the first day. */
export interface CompanyOwnershipDraft {
  ownerCompanyId: string;
  ownershipPercent: number | null;
  startDate: string;
}

/** The shape a record's id has; a draft naming anything else names no company. */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The refusals both the window and the server read, so the two say one thing. */
export const OWNERSHIP_REFUSALS = {
  noOwner: 'Choose the company that owns this one.',
  self: 'A company cannot own itself; choose another company as its owner.',
  percent: 'A percent owned is more than 0 and at most 100, or is left empty.',
  noStart: 'An ownership needs the day it began.',
  noEnd: 'Ending an ownership needs its last day.',
  endBeforeStart: 'The last day owned cannot fall before the day the ownership began.',
  endAfterToday: 'The last day owned is today or earlier; end the ownership once that day comes.',
} as const;

/** A percent the table takes: more than zero and at most 100 (its own check), or none. */
export function ownershipPercentProblem(percent: unknown): string | null {
  if (percent === null || percent === undefined) return null;
  if (typeof percent !== 'number' || !Number.isFinite(percent) || percent <= 0 || percent > 100) return OWNERSHIP_REFUSALS.percent;
  return null;
}

/** The first refusal a draft meets on its own, or null; the cycle and the overlap are the server's, which reads the other links. */
export function companyOwnershipDraftProblem(draft: Partial<CompanyOwnershipDraft>, ownedCompanyId: string): string | null {
  if (typeof draft.ownerCompanyId !== 'string' || draft.ownerCompanyId === '') return OWNERSHIP_REFUSALS.noOwner;
  // A malformed id is no company, refused here in words and never left to reach the database.
  if (!UUID_SHAPE.test(draft.ownerCompanyId)) return OWNERSHIP_REFUSALS.noOwner;
  if (draft.ownerCompanyId === ownedCompanyId) return OWNERSHIP_REFUSALS.self;
  const percent = ownershipPercentProblem(draft.ownershipPercent);
  if (percent) return percent;
  if (typeof draft.startDate !== 'string' || !isCalendarDay(draft.startDate)) return OWNERSHIP_REFUSALS.noStart;
  return null;
}

/** The refusal an end meets, or null: a last day on or after the start and no later than the Organization's today. */
export function ownershipEndProblem(startDate: string, endDate: unknown, today: string): string | null {
  if (typeof endDate !== 'string' || !isCalendarDay(endDate)) return OWNERSHIP_REFUSALS.noEnd;
  if (endDate < startDate) return OWNERSHIP_REFUSALS.endBeforeStart;
  if (endDate > today) return OWNERSHIP_REFUSALS.endAfterToday;
  return null;
}

/** A percent as a line reads it: 60%, 33.33%. */
export function ownershipPercentText(percent: number | null | undefined): string | null {
  if (percent === null || percent === undefined) return null;
  return `${Number(percent.toFixed(2))}%`;
}

/** The owned company's owners and its add route. */
export function companyOwnersPath(companyId: string): string {
  return `/company-ownership/company/${encodeURIComponent(companyId)}`;
}

/** One ownership's End, which takes the last day owned. */
export function companyOwnershipEndPath(linkId: string): string {
  return `/company-ownership/${encodeURIComponent(linkId)}/end`;
}
